/**
 * 担当案件テーブル（開発 view）に「DLE 評価の行を必ず出す」ための純粋関数群。
 *
 * 開発 view の案件一覧は **ステータスが `完了` / `保留中` でない母票**しか出さない
 * （`getMyProjectsBoard` の WHERE 句）。一方 DLE 評価は `開発完了` 以降が対象なので、
 * **`完了` になった案件は一覧から落ちたまま未回答だけが残る**、という取りこぼしが
 * 構造的に起きる。ここで「行が描かれない対象」を拾い上げ、テーブル末尾に足す。
 *
 * **回答済みも 1 週間は残す**のが要点。回答した直後に行が消えると、自分の回答が通ったことを
 * 一覧で確認できない（送信できたのか分からないまま終わる）。保持期間はデザイン view の
 * 子票行と同じ規則（`settled-row-retention.ts`）を使う。
 *
 * @graph-stack product-innovation-lab
 * @graph-domain UX
 * @graph-business 案件一覧から落ちた母票（完了済み）の DLE 評価を行に起こし、回答済みは 1 週間で落とす
 * @graph-connects isStaleAnsweredRow [calls] 回答日からの保持期間判定
 */
import type { DleEvaluationTask } from '../../../types/dle-evaluation.js';
import { isStaleAnsweredRow } from './settled-row-retention.js';

/**
 * 指定した index を先頭に持ってくる。モーダルは先頭から順に回答していくので、
 * 「クリックした件から始めて、残りも続けて消化できる」状態にする。
 * @graph-connects none
 */
export function orderedDleTasksFrom(
  tasks: DleEvaluationTask[],
  index: number,
): DleEvaluationTask[] {
  if (index <= 0) return tasks;
  return [...tasks.slice(index), ...tasks.slice(0, index)];
}

/**
 * この行を一覧から落とすか。
 *
 * **未回答は 1 件も落とさない**のが不変条件 — 落とすと回答経路が消える。回答済みは
 * 回答日から保持期間を超えたときだけ落とす。回答日が取れない回答済みは落とさない
 * （判定材料が無い行を黙って消さない）。
 * @graph-connects isStaleAnsweredRow [calls] 回答日からの保持期間判定
 */
export function isStaleAnsweredTask(task: DleEvaluationTask, today: string): boolean {
  return isStaleAnsweredRow(task, today);
}

/**
 * 既に行が描かれている母票キーの集合を渡すと、**そこに含まれない対象**を返す。
 *
 * 未回答に加えて「回答してから 1 週間以内」の回答済みも返す。回答済みの行は
 * `DleSurveyCell` が「回答済」バッジを出すので、回答が通ったことをその場で確認できる。
 * 並びは API が決めた順（未回答が先・回答可能になった日の新しい順）をそのまま保つ。
 * @graph-connects isStaleAnsweredTask [calls] 回答済み行の除外判定
 */
export function orphanDleTasks(
  tasks: DleEvaluationTask[],
  renderedParentKeys: ReadonlySet<string>,
  today: string,
): DleEvaluationTask[] {
  return tasks.filter(
    (task) => !renderedParentKeys.has(task.parentIssueKey) && !isStaleAnsweredTask(task, today),
  );
}
